import { useCallback, useEffect, useRef } from "react"

import { Col, Divider, Row } from "antd"
import cls from "classname"
import { connect, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { getCompareParams } from "../../Components/Filters/CompareSelect/presets"
import { useDemoBannerVisible } from "../../hooks/useDemoBannerVisible"
import { getDates } from "../../Utils/utils"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import ByCardTypeBlock from "./components/ByCardTypeBlock"
import ByCountryBlock from "./components/ByCountryBlock"
import ByReasonCodeBlock from "./components/ByReasonCodeBlock"
import ChargebackOlympics from "./components/ChargebackOlympics"
import ChargebackOverview from "./components/ChargebackOverview"
import Header from "./components/Header/Header"
import PreventionRate from "./components/PreventionRate"
import { ProcessorRiskLevel } from "./components/ProcessorRiskLevel"
import StatisticsBlock from "./components/StatisticsCard/StatisticsBlock"
import { useHealthDefaultDataSource } from "./hooks/useHealthDataSource"
import Creators from "./reducer"

import "./styles.less"

const HealthOverviewPage = ({
  nav: { isMobile },
  isAdmin,
  userInfo,
  filters,
  // usersViewForAdmin,
  // isBusinessHealthAvailable,
  // mainInfo,
  ...props
}) => {
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const helpRef = useRef({ firstLoading: true })
  const { dataSource } = useSelector((state) => state.health)
  const { current_business_account_id, is_demo } = userInfo
  const { isShowDemoData } = useDemoDataToggle()
  const isDemoBannerVisible = useDemoBannerVisible()
  const [searchParams] = useSearchParams()
  const business_account = isAdmin
    ? searchParams.get("business_account_id")
    : current_business_account_id

  const requestedSource = searchParams.get("source")

  useHealthDefaultDataSource({
    fetchedBA: businessAccount,
    currentBAId: business_account,
    requestedSource
  })

  useEffect(() => {
    if (!requestedSource) return
    props.changeFilters({ compare: null, lastCompare: null })
  }, [requestedSource])

  const pageData = {
    title: isAdmin ? `Health for ${businessAccount?.name}` : "Health overview",
    description: "View the health of your business.",
    buttonText: "Watch tutorial",
    videoUrl: "https://www.youtube.com/watch?v=RbwXgn51EWM"
  }

  const handleChangeFilter = useCallback((newFilters) => {
    if (Array.isArray(newFilters.period)) {
      props.changeFilters({
        ...newFilters,
        period: { type: "custom", period: newFilters.period }
      })
    } else {
      props.changeFilters(newFilters)
    }
  }, [])

  useEffect(() => {
    document.title = "Health - Chargeback"
  }, [])

  useEffect(() => {
    if (filters?.period && dataSource) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare, period_query)
      const base = {
        ...period_query,
        ...compare_query,
        business_account,
        source: dataSource
      }
      props.getHealthMainInfo(base)
      props.getDataByCardType(base)
      props.getDataOlympics(base)
      props.getRiskLevel(base)
      // if (dataSource !== "shopify") {
      props.getDataByCountry(base)
      // };
      props.getDataByReasonCode(base)
      props.getFilterOptions({ business_account, source: dataSource })

      if (helpRef.current.firstLoading) helpRef.current.firstLoading = false
    }
  }, [filters?.period, filters?.compare, business_account, isShowDemoData, dataSource])

  useEffect(() => {
    if (filters?.period && dataSource) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare, period_query)
      props.getHealthOverview({
        ...period_query,
        ...compare_query,
        chart_type: filters?.overviewType,
        business_account,
        source: dataSource
      })
    }
  }, [
    filters?.period,
    filters?.compare,
    filters?.overviewType,
    business_account,
    isShowDemoData,
    dataSource
  ])

  return (
    <div>
      <div
        className={cls("health-page", {
          // locked: !isBusinessHealthAvailable && !isAdmin,
          "is-admin-view": isAdmin,
          "is-mobile": isMobile,
          "demo-banner-visible": isDemoBannerVisible
        })}
      >
        <Header
          filters={filters}
          handleChangeFilter={handleChangeFilter}
          type="overview"
          businessAccountId={business_account}
        />
        {isAdmin && <Divider style={{ margin: 0 }} />}
        <div className="page-content" style={!isAdmin ? { paddingTop: 0 } : {}}>
          <Row gutter={[isMobile ? 16 : 32, isMobile ? 16 : 32]} align={"stretch"}>
            <Col span={24}>
              <StatisticsBlock />
            </Col>
            <Col span={24}>
              <ChargebackOverview filters={filters} handleChangeFilter={handleChangeFilter} />
            </Col>
            <Col span={24}>
              <ByCardTypeBlock />
            </Col>
            <Col span={24}>
              <ByReasonCodeBlock />
            </Col>
            <Col span={24}>
              <ByCountryBlock />
            </Col>
            <Col span={24}>
              <PreventionRate />
            </Col>
            <Col xs={24} sm={24} md={24} lg={14} xl={12}>
              <ChargebackOlympics />
            </Col>
            <Col xs={24} sm={24} md={24} lg={10} xl={12}>
              <ProcessorRiskLevel />
            </Col>
          </Row>
        </div>
      </div>
      {/* {!isAdmin && <HealthBanner />} */}
    </div>
  )
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,
  filters: state.health.filters,
  isBusinessHealthAvailable: state.health.mainInfo.is_business_health_available,
  mainInfo: state.health.mainInfo,
  businessAccount: state.user.businessAccount
})

const mapDispatchToProps = (dispatch) => ({
  changeFilters: (filters) => dispatch(Creators.healthSetProp("filters", filters)),
  getHealthMainInfo: (params) => dispatch(Creators.getHealthMainInfoRequest(params)),
  getHealthOverview: (params) => dispatch(Creators.getHealthOverviewRequest(params)),
  getDataByCardType: (params) => dispatch(Creators.getDataByCardTypeRequest(params)),
  getDataByCountry: (params) => dispatch(Creators.getDataByCountryRequest(params)),
  getDataByReasonCode: (params) => dispatch(Creators.getDataByReasonCodeRequest(params)),
  getDataOlympics: (params) => dispatch(Creators.getDataOlympicsRequest(params)),
  getRiskLevel: (params) => dispatch(Creators.getRiskLevelRequest(params)),
  getFilterOptions: (params) => dispatch(Creators.getFilterOptionsRequest(params)),
  healthSetProp: (prop, value) => dispatch(Creators.healthSetProp(prop, value))
})

export default connect(mapStateToProps, mapDispatchToProps)(HealthOverviewPage)
